import { Badge, Card, CardBody, CardHeader, CardFooter, Button, MultiSelect, Icon, useToast, DataTable, Action, Checkbox } from "@aidkitorg/component-library";
import { DocumentLibraryItem } from "@aidkitorg/types/lib/convoai/documentLibrary.types";
import { OpenAI } from "openai";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePost } from "../../API";
import { useAsyncEffect } from "../../Hooks/AsyncEffect";
import type { Model as MessageTemplate } from '@aidkitorg/aidkit/lib/convoai/schemas/MessageTemplate';
import { ColumnDef } from "@tanstack/react-table";
import { limitConcurrentRequests } from "@aidkitorg/types/lib/util";

const reasoning: OpenAI.ReasoningEffort[] = ['minimal', 'low', 'medium', 'high'];

type RunPermutation = {
  id: string,
  configId: string,
  template: MessageTemplate,
  model: {
    name: OpenAI.ChatModel,
    params?: { reasoning?: NonNullable<OpenAI.ReasoningEffort> }
  },
  document: DocumentLibraryItem,
  phase: 'NOT_SUBMITTED' | 'QUEUED' | 'STARTED' | 'FINISHED' | 'FAILED' | 'TIMEOUT',
  result?: string,
  error?: string,
  taskId?: string,
  conversationId?: string,
  evaluationScore?: { passed: number, failed: number, total: number },
  startedAt?: number,
  completedAt?: number,
};


const MODEL_SELECTION = {
  'gpt-5': { reasoning },
  'gpt-5-nano': { reasoning },
  'gpt-5-mini': { reasoning },
  'gpt-4.1': {},
  'gpt-4.1-mini': {},
  'gpt-4.1-nano': {}
};

export function MessageTemplateRunner() {

  const getAllMessageTemplates = usePost('/program/admin/prompts/scan');

  type RunConfig = {
    id: string,
    models: {
      name: OpenAI.ChatModel,
      params?: { reasoning?: NonNullable<OpenAI.ReasoningEffort> }
    }[],
    templates: MessageTemplate[],
    categories: string[],
  };

  const getAllDocuments = usePost('/ai/document_library/get_all');
  const [messageTemplates, setMessageTemplates] = useState<Record<string, MessageTemplate>>({});
  const [documentsByCategory, setDocumentsByCategory] = useState<Record<string, DocumentLibraryItem[]>>({});
  const [models] = useState<Record<string, { reasoning?: typeof reasoning }>>(MODEL_SELECTION);
  const [runConfigs, setRunConfigs] = useState<RunConfig[]>([]);
  const [permutations, setPermutations] = useState<RunPermutation[]>([]);
  const [tablePagination, setTablePagination] = useState<{ pageIndex: number, pageSize: number }>({ pageIndex: 0, pageSize: 20 });
  const [hideFailed, setHideFailed] = useState(true);

  const [selectedMessageTemplates, setSelectedMessageTemplates] = useState<{ label: string | JSX.Element, value: string }[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<{ label: string | JSX.Element, value: string }[]>([]);
  const [selectedModels, setSelectedModels] = useState<{ label: string | JSX.Element, value: string }[]>([]);
  useAsyncEffect(async () => {

    const templates = await getAllMessageTemplates({});
    setMessageTemplates(templates.items.reduce((acc, template) => {
      let candidate = acc[template.name];
      if (!candidate || template.version > (candidate.version ?? 0)) {
        candidate = template;
      }
      return {
        ...acc,
        [template.name]: candidate
      }
    }, {} as Record<string, MessageTemplate>));

    const docs = await getAllDocuments({ templates: 'omit' });
    const docsByCategory: typeof documentsByCategory = {};
    for (const doc of (docs.data ?? [])) {
      docsByCategory[doc.category] = (docsByCategory[doc.category] || []).concat(doc);
    }

    setDocumentsByCategory(docsByCategory);
  }, []);

  const generatePermutations = useCallback((config: RunConfig): RunPermutation[] => {
    const perms: RunPermutation[] = [];
    let permId = 0;

    for (const template of config.templates) {
      for (const model of config.models) {
        for (const category of config.categories) {
          for (const document of documentsByCategory[category] || []) {

            perms.push({
              id: `${config.id}-${permId++}`,
              configId: config.id,
              template,
              model,
              document,
              phase: 'NOT_SUBMITTED'
            });
          }
        }
      }
    }
    return perms;
  }, [documentsByCategory]);

  const getModelDisplayName = useCallback((model: { name: OpenAI.ChatModel, params?: { reasoning?: NonNullable<OpenAI.ReasoningEffort> } }) => {
    let name = model.name;
    if (model.params?.reasoning) {
      name += `-${model.params.reasoning}`;
    }
    return name;
  }, []);

  function StatusCell({ permutation, onUpdate }: { permutation: RunPermutation, onUpdate: (next: Partial<RunPermutation>) => void }) {
    const checkStatus = usePost('/program/admin/async_job_status');
    const mountedRef = useRef(true);
    useEffect(() => {
      mountedRef.current = true;
      return () => { mountedRef.current = false; };
    }, []);

    useEffect(() => {
      if (!permutation.taskId) return;
      if (!(permutation.phase === 'QUEUED' || permutation.phase === 'STARTED')) return;
      const INTERVAL_MS = 3000;
      const FIFTEEN_MIN_MS = 15 * 60 * 1000;
      let intervalId: any = setInterval(async () => {
        if (!mountedRef.current) return;
        try {
          const { status } = await checkStatus({ taskId: permutation.taskId! });
          const now = Date.now();
          if (status === 'STARTED' && permutation.startedAt && (now - permutation.startedAt > FIFTEEN_MIN_MS)) {
            onUpdate({ phase: 'TIMEOUT', completedAt: permutation.startedAt + FIFTEEN_MIN_MS, error: 'Timed out after 15 minutes' });
            clearInterval(intervalId);
            return;
          }
          const nextPhase: RunPermutation['phase'] = (status === 'QUEUED' || status === 'STARTED' || status === 'FINISHED' || status === 'FAILED') ? status : 'NOT_SUBMITTED';
          if (nextPhase === 'FINISHED' || nextPhase === 'FAILED') {
            onUpdate({ phase: nextPhase, completedAt: permutation.completedAt ?? Date.now() });
            clearInterval(intervalId);
            return;
          }
          onUpdate({ phase: nextPhase });
        } catch (e) {
        }
      }, INTERVAL_MS);
      return () => clearInterval(intervalId);
    }, [permutation.taskId, permutation.phase, permutation.startedAt, permutation.completedAt, checkStatus, onUpdate]);

    const { phase, error } = permutation;
    if (phase === 'QUEUED') return <Icon name="ClockIcon" className="text-gray-600" role="informative" title="Queued" />;
    if (phase === 'STARTED') return <Icon name="ArrowPathIcon" className="text-blue-600 animate-spin" role="informative" title="Running" />;
    if (phase === 'FINISHED') return <Icon name="CheckIcon" className="text-green-600" />;
    if (phase === 'TIMEOUT') return (
      <span className="text-xs text-amber-600" title={error || 'Timed out'}>
        Timeout
      </span>
    );
    if (phase === 'FAILED') return (
      <span className="text-xs text-red-600" title={error || undefined}>
        Failed
      </span>
    );
    return <Icon name="EllipsisHorizontalIcon" className="text-gray-400" role="informative" title="Not started" />;
  }

  const permutationColumns = useMemo<ColumnDef<RunPermutation>[]>(() => [
    {
      id: 'templateName',
      accessorKey: 'template.name',
      header: 'Template',
      cell: ({ row }) => (
        <div className="font-medium">
          {row.original.template.name}
          <span className="text-xs text-gray-500 ml-1">v{row.original.template.version ?? 0}</span>
        </div>
      )
    },
    {
      accessorKey: 'model',
      header: 'Model',
      cell: ({ row }) => (
        <span className="text-xs">{getModelDisplayName(row.original.model)}</span>
      )
    },
    {
      accessorKey: 'document.category',
      header: 'Category',
      cell: ({ row }) => (
        <span className="text-xs">{row.original.document.category}</span>
      )
    },
    {
      accessorKey: 'document.variations',
      header: 'Variations',
      cell: ({ row }) => {
        const variations = row.original.document.metadata?.variations || [];
        if (variations.length === 0) {
          return <span className="text-xs text-gray-400">None</span>;
        }
        const variationText = variations.join(', ');
        return (
          <div
            className="text-xs text-gray-700 max-w-xs truncate"
            title={variationText}
          >
            {variationText}
          </div>
        );
      }
    },
    {
      accessorKey: 'progress',
      header: 'Progress',
      cell: ({ row }) => (
        <StatusCell
          permutation={row.original}
          onUpdate={(next) => setPermutations(prev => prev.map(p => p.id === row.original.id ? { ...p, ...next } : p))}
        />
      )
    },
    {
      accessorKey: 'completedAt',
      header: 'Completed',
      cell: ({ row }) => {
        const { phase, completedAt } = row.original;
        if ((phase === 'FINISHED' || phase === 'FAILED' || phase === 'TIMEOUT') && completedAt) {
          try {
            return <span className="text-xs text-gray-700">{new Date(completedAt).toLocaleString()}</span>;
          } catch {
            return <span className="text-xs text-gray-400">-</span>;
          }
        }
        return <span className="text-xs text-gray-400">-</span>;
      }
    },

    {
      accessorKey: 'result',
      header: 'Result',
      cell: ({ row }) => {
        const { result, error, phase, evaluationScore } = row.original;
        if (phase === 'FAILED' && error) {
          return (
            <div className="text-red-600 text-xs truncate max-w-32" title={error}>
              {error}
            </div>
          );
        }
        if (phase === 'FINISHED' && evaluationScore) {
          return (
            <div className="text-xs space-y-1">
              <div className="text-green-600">{evaluationScore.passed} passed</div>
              <div className="text-red-600">{evaluationScore.failed} failed</div>
              <div className="text-gray-600">{evaluationScore.total} total</div>
            </div>
          );
        }
        if (phase === 'FINISHED' && result) {
          try {
            const parsed = JSON.parse(result);
            const fieldCount = Object.keys(parsed).length;
            return (
              <div className="text-xs text-gray-600" title={result}>
                {fieldCount} fields
              </div>
            );
          } catch {
            return (
              <div className="text-xs text-gray-600 truncate max-w-32" title={result}>
                {result.substring(0, 20)}...
              </div>
            );
          }
        }
        return '-';
      }
    }
  ], [getModelDisplayName]);

  const runEvaluations = usePost('/ai/document_library/queue_evaluate');
  const getEvaluationHistory = usePost('/ai/document_library/history');
  const { toast } = useToast();

  const [previewDocument, setPreviewDocument] = useState<DocumentLibraryItem | null>(null);


  // Restore previous runs for the current user from history (server-side filtered)
  useAsyncEffect(async () => {
    if (Object.keys(messageTemplates).length === 0) return;
    const allDocs = Object.values(documentsByCategory).flat();
    if (allDocs.length === 0) return;

    const sixHoursAgo = new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString();
    const historyResp = await getEvaluationHistory({ mine: true, createdAfter: sixHoursAgo });
    const jobEntries: any[] = historyResp?.data ?? [];

    const rowsToAdd: RunPermutation[] = [];
    let counter = 0;

    for (const job of jobEntries) {
      if (job?.path !== '/ai/document_library/evaluate') continue;
      const params = job?.params || {};
      const templates = Array.isArray(params.templates) ? params.templates : [];
      const docIds: string[] = Array.isArray(params.documentIds) ? params.documentIds : [];

      for (const t of templates) {
        const template = messageTemplates[t?.name];
        if (!template) continue;
        const version = typeof t?.version === 'number' ? t.version : template.version;

        const modelsArr: { name: OpenAI.ChatModel, params?: { reasoning?: NonNullable<OpenAI.ReasoningEffort> } }[] =
          Array.isArray(t?.models) ? t.models.map((m: any) => ({
            name: m?.name as OpenAI.ChatModel,
            params: m?.params?.reasoning ? { reasoning: m.params.reasoning as NonNullable<OpenAI.ReasoningEffort> } : undefined
          })) : [];

        for (const model of modelsArr) {
          if (!model?.name) continue;
          for (const docId of docIds) {
            const document = allDocs.find(d => d.id === docId);
            if (!document) continue;

            const statusText = String(job?.status || '').toUpperCase();

            const serializedResult = job?.result ? (
              typeof job.result === 'string' ? job.result : JSON.stringify(job.result)
            ) : undefined;

            // Determine phase; map server-side timeout-as-failed to TIMEOUT for UI clarity
            let phase: RunPermutation['phase'] = (statusText || 'NOT_SUBMITTED') as RunPermutation['phase'];
            if (phase === 'FAILED' && job?.started_at && !job?.finished_at && !job?.errored_at) {
              phase = 'TIMEOUT';
            }

            const startedAtMs = job?.started_at ? new Date(job.started_at).getTime() : undefined;
            const finishedAtMs = job?.finished_at ? new Date(job.finished_at).getTime() : undefined;
            const erroredAtMs = job?.errored_at ? new Date(job.errored_at).getTime() : undefined;
            const timeoutAtMs = (phase === 'TIMEOUT' && startedAtMs) ? (startedAtMs + (15 * 60 * 1000)) : undefined;

            rowsToAdd.push({
              id: `history-job-${job.id}-${counter++}`,
              configId: 'history',
              template: { ...template, version },
              model,
              document,
              phase,
              result: serializedResult,
              taskId: typeof job?.id === 'string' ? job.id : undefined,
              startedAt: startedAtMs,
              completedAt: finishedAtMs ?? erroredAtMs ?? timeoutAtMs
            });
          }
        }
      }
    }

    if (rowsToAdd.length) {
      setPermutations(prev => [...rowsToAdd, ...prev]);
    }
  }, [messageTemplates, documentsByCategory]);

  const runSinglePermutation = useCallback(async (permutation: RunPermutation) => {
    if (permutation.phase === 'QUEUED' || permutation.phase === 'STARTED') {
      toast({ description: 'Permutation is already running', variant: 'error' });
      return;
    }

    // Set permutation to running
    setPermutations(prev => prev.map(p =>
      p.id === permutation.id ? { ...p, phase: 'QUEUED', startedAt: Date.now() } : p
    ));

    try {
      const evaluationResults = await runEvaluations({
        evaluation: {
          templates: [{
            models: [permutation.model],
            name: permutation.template.name,
            version: permutation.template.version,
          }],
          documentIds: [permutation.document.id]
        }
      });

      if (evaluationResults.error) {
        setPermutations(prev => prev.map(p =>
          p.id === permutation.id ? { ...p, phase: 'FAILED', error: 'API request failed', completedAt: Date.now() } : p
        ));
        toast({ description: 'Failed to run permutation', variant: 'error' });
      } else if (evaluationResults.data?.taskId) {
        const taskId = evaluationResults.data.taskId;

        setPermutations(prev => {
          const next = prev.map<RunPermutation>(p =>
            p.id === permutation.id ? ({ ...p, taskId, phase: 'QUEUED' as RunPermutation['phase'], startedAt: p.startedAt ?? Date.now() } as RunPermutation) : p
          );
          return next;
        });

        toast({ description: 'Started individual permutation', variant: 'success' });
      } else {
        setPermutations(prev => prev.map(p =>
          p.id === permutation.id ? { ...p, phase: 'FAILED', error: 'No task ID returned', completedAt: Date.now() } : p
        ));
        toast({ description: 'Invalid response', variant: 'error' });
      }
    } catch (error) {
      setPermutations(prev => prev.map(p =>
        p.id === permutation.id ? { ...p, phase: 'FAILED', error: 'Network error', completedAt: Date.now() } : p
      ));
      toast({ description: 'Network error', variant: 'error' });
    }
  }, [runEvaluations, toast]);

  const removePermutation = useCallback((permutation: RunPermutation) => {
    setPermutations(prev => prev.filter(p => p.id !== permutation.id));

    toast({ description: 'Permutation removed', variant: 'success' });
  }, [toast]);

  const permutationActions: Action<RunPermutation>[] = [
    {
      label: 'Preview Document',
      icon: 'EyeIcon',
      onClick: (permutation: RunPermutation) => setPreviewDocument(permutation.document)
    },
    {
      label: 'Run Individual',
      icon: 'PlayIcon',
      onClick: runSinglePermutation,
      hideWhen: (permutation: RunPermutation) => permutation.phase === 'QUEUED' || permutation.phase === 'STARTED'
    },
    {
      label: 'Remove',
      icon: 'XMarkIcon',
      onClick: removePermutation,
      hideWhen: (permutation: RunPermutation) => permutation.phase === 'QUEUED' || permutation.phase === 'STARTED'
    }
  ];

  const filteredPermutations = useMemo(() => {
    return hideFailed ? permutations.filter(p => p.phase !== 'FAILED' && p.phase !== 'TIMEOUT') : permutations;
  }, [hideFailed, permutations]);

  const runAllUnsubmitted = useCallback(async () => {
    const toRun = permutations.filter(p => p.phase === 'NOT_SUBMITTED');
    if (toRun.length === 0) return;

    // Optimistically mark NOT_SUBMITTED permutations as queued
    setPermutations(prev => prev.map(p => (p.phase === 'NOT_SUBMITTED') ? ({ ...p, phase: 'QUEUED', startedAt: Date.now() }) : p));

    await limitConcurrentRequests(toRun.map((permutation) => async () => {
      try {
        const evaluationResults = await runEvaluations({
          evaluation: {
            templates: [{
              models: [permutation.model],
              name: permutation.template.name,
              version: permutation.template.version,
            }],
            documentIds: [permutation.document.id]
          }
        });

        if (evaluationResults.error) {
          setPermutations(prev => prev.map(p =>
            p.id === permutation.id ? { ...p, phase: 'FAILED', error: 'API request failed', completedAt: Date.now() } : p
          ));
        } else if (evaluationResults.data?.taskId) {
          const taskId = evaluationResults.data.taskId;
          setPermutations(prev => prev.map<RunPermutation>(p =>
            p.id === permutation.id ? ({ ...p, taskId, phase: 'QUEUED' as RunPermutation['phase'], startedAt: p.startedAt ?? Date.now() } as RunPermutation) : p
          ));
        } else {
          setPermutations(prev => prev.map(p =>
            p.id === permutation.id ? { ...p, phase: 'FAILED', error: 'No task ID returned', completedAt: Date.now() } : p
          ));
        }
      } catch (error) {
        setPermutations(prev => prev.map(p =>
          p.id === permutation.id ? { ...p, phase: 'FAILED', error: 'Network error', completedAt: Date.now() } : p
        ));
      }
    }), 25);

    toast({ description: `Queued ${toRun.length} individual evaluations`, variant: 'success' });
  }, [permutations, runEvaluations, toast]);

  const addRunConfiguration = useCallback(() => {
    if (selectedMessageTemplates.length === 0 || selectedCategories.length === 0 || selectedModels.length === 0) {
      toast({ description: 'Please select templates, categories, and models', variant: 'error' });
      return;
    }

    const newConfig: RunConfig = {
      id: Date.now().toString(),
      models: selectedModels.map(model => {
        const [modelName, reasoning] = model.value.split('|');
        return {
          name: modelName as OpenAI.ChatModel,
          params: reasoning ? {
            reasoning: reasoning as NonNullable<OpenAI.ReasoningEffort>
          } : undefined
        };
      }),
      templates: selectedMessageTemplates.map(template => messageTemplates[template.value]),
      categories: selectedCategories.map(category => category.value)
    };

    const newPermutations = generatePermutations(newConfig);

    setRunConfigs(prev => [...prev, newConfig]);
    setPermutations(prev => [...prev, ...newPermutations]);

    // Clear selections
    setSelectedMessageTemplates([]);
    setSelectedCategories([]);
    setSelectedModels([]);
  }, [selectedMessageTemplates, selectedCategories, selectedModels, messageTemplates, generatePermutations, toast]);

  const clearAllPermutations = useCallback(() => {
    setPermutations(prev => prev.filter(p => p.phase !== 'NOT_SUBMITTED'));
  }, []);

  const hasRunningPermutations = permutations.some(p => p.phase === 'QUEUED' || p.phase === 'STARTED');
  const hasUnsubmitted = permutations.some(p => p.phase === 'NOT_SUBMITTED');
  const unsubmittedCount = useMemo(() => permutations.filter(p => p.phase === 'NOT_SUBMITTED').length, [permutations]);

  // Keep current page when data changes; clamp if needed
  useEffect(() => {
    const totalRows = permutations.length;
    const pageCount = tablePagination.pageSize > 0 ? Math.ceil(totalRows / tablePagination.pageSize) : 0;
    if (tablePagination.pageIndex >= pageCount && pageCount > 0) {
      setTablePagination(prev => ({ ...prev, pageIndex: Math.max(0, pageCount - 1) }));
    }
  }, [permutations.length, tablePagination.pageSize, tablePagination.pageIndex]);

  return <div className="space-y-4">
    <Card>
      <CardHeader title="New Run Configuration" />
      <CardBody className="py-2">
        <MultiSelect
          label="Message Templates"
          searchable
          filterFunction={(opt, search) => {
            const { name } = messageTemplates[opt.value];
            return name.includes(search);
          }}
          description="Select message templates you want to analyze documents with"
          value={selectedMessageTemplates}
          onChange={setSelectedMessageTemplates}
          options={Object.entries(messageTemplates).map(([name]) => ({
            label: <span>{name}</span>,
            value: name
          }))}
        />
        <MultiSelect
          label="Document Categories"
          description="Select documents want to analyze with message templates"
          searchable
          value={selectedCategories}
          onChange={setSelectedCategories}
          options={Object.keys(documentsByCategory).map(category => ({
            label: category,
            value: category
          }))}
        />
        <MultiSelect
          label="LLMs"
          description="Select the LLMs you want to run with"
          searchable
          value={selectedModels}
          onChange={setSelectedModels}
          options={Object.entries(models).flatMap(([model, { reasoning }]) =>
            reasoning ? reasoning.map(effort => ({
              label: `${model}-${effort}`,
              value: `${model}|${effort}`
            })) : [{
              label: model,
              value: model
            }])}
        />
      </CardBody>
      <CardFooter>
        <Button
          onClick={addRunConfiguration}
          isDisabled={selectedMessageTemplates.length === 0 || selectedCategories.length === 0 || selectedModels.length === 0}
        >
          Add Configuration
        </Button>
      </CardFooter>
    </Card>

    {permutations.length > 0 && (
      <Card>
        <CardHeader
          title={`Evaluation Runs`}
          actionButton={
            <div className="flex items-center gap-3">
              <Checkbox label="Hide failed" checked={hideFailed} onChange={(v: any) => setHideFailed(!!v)} />
              <Button
                onClick={runAllUnsubmitted}
                isDisabled={!hasUnsubmitted || hasRunningPermutations}
                icon={<Icon name="PlayIcon" />}
              >
                {hasRunningPermutations ? 'Running...' : 'Run Unsubmitted'}
                {unsubmittedCount > 0 && (
                  <Badge variant="neutral" className="ml-2 text-xs">{unsubmittedCount}</Badge>
                )}
              </Button>
              <Button
                variant="secondary"
                onClick={clearAllPermutations}
                isDisabled={!hasUnsubmitted}
                icon={<Icon name="XMarkIcon" />}
              >
                Clear All
              </Button>
            </div>
          }
        />
        <CardBody className="p-0">
          <DataTable
            columns={permutationColumns}
            data={filteredPermutations}
            actions={permutationActions}
            enableSearch
            searchLabel="Search permutations..."
            pageSize={25}
          />
          {/* Centralized polling replaces per-row TaskWatcher */}
        </CardBody>
      </Card>
    )}

    {/* Document Preview Modal */}
    {previewDocument && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setPreviewDocument(null)}>
        <div className="bg-white rounded-lg p-6 max-w-4xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Document Preview</h3>
            <Button
              variant="secondary"
              size="sm"
              icon={<Icon name="XMarkIcon" />}
              onClick={() => setPreviewDocument(null)}
            />
          </div>

          <div className="space-y-4">
            {/* Document Image */}
            {(previewDocument as any).s3_url && (
              <div className="mb-4">
                <strong className="block mb-2">Document Image:</strong>
                <div className="border rounded-lg overflow-hidden bg-gray-50">
                  <img
                    src={(previewDocument as any).s3_url}
                    alt={previewDocument.original_filename}
                    className="w-full h-auto max-h-96 object-contain"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      e.currentTarget.nextElementSibling?.classList.remove('hidden');
                    }}
                  />
                  <div className="hidden p-4 text-center text-gray-500">
                    <Icon name="DocumentIcon" className="w-12 h-12 mx-auto mb-2" />
                    <p>Image could not be loaded</p>
                  </div>
                </div>
              </div>
            )}

            {/* Document Metadata */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <strong>Filename:</strong> {previewDocument.original_filename}
              </div>
              <div>
                <strong>Category:</strong> {previewDocument.category}
              </div>
            </div>

            {previewDocument.metadata?.variations && previewDocument.metadata.variations.length > 0 && (
              <div>
                <strong>Variations:</strong>
                <div className="flex flex-wrap gap-1 mt-1">
                  {previewDocument.metadata.variations.map((variation, index) => (
                    <Badge key={index} variant="neutral" className="text-xs">
                      {variation}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {previewDocument.metadata?.fields && previewDocument.metadata.fields.length > 0 && (
              <div>
                <strong>Expected Fields:</strong>
                <div className="mt-2 space-y-2">
                  {previewDocument.metadata.fields.map((field, index) => (
                    <div key={index} className="p-3 bg-gray-50 rounded-lg text-sm">
                      <div className="font-medium text-gray-900">{field.name}</div>
                      <div className="text-gray-600 mt-1">{field.value}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    )}


  </div>;
}
